import type { Edge, Node } from "@xyflow/react";
import { z } from "zod";
import type { BatchChangeRequestsJobType, DataImportPipelineJobSubmitOutput, DryRunOptions } from "~/common/types";
import { PropertyAttributeTypeId, PropertySchemaType } from "~/common/types";
import type { BatchChangeRequestsJob_DefaultFragment, GetBatchChangeRequestsJobQuery } from "./gqls";
import type { SheetColumn, SheetState, SheetVariantKeys } from "./hooks";

const zSelectOption = z.object({
  value: z.string(),
  label: z
    .object({
      en: z.string().optional(),
      ja: z.string().optional(),
    })
    .optional(),
});

export const zSelectOptions = z.array(zSelectOption);
export type SelectOption = z.infer<typeof zSelectOption>;

const zURLAllowListItem = z.object({
  value: z.string(),
  regex: z.string(),
});
export const zURLAllowList = z.array(zURLAllowListItem);
export type URLAllowList = z.infer<typeof zURLAllowList>;

const zPropertySchemaAssociationFormData = z.object({
  id: z.string().optional(),
  name: z.string().readonly(),
  description: z.string().optional(),
  position: z.number().optional(),
  required: z.boolean(),
  unique: z.boolean(),
  propertyAttributeTypeId: z.nativeEnum(PropertyAttributeTypeId).nullish(),
  propertySchema: z.object({
    id: z.string().optional(),
    propertyTypeId: z.nativeEnum(PropertySchemaType),
    selectOptions: zSelectOptions,
    urlAllowList: zURLAllowList.optional(),
  }),
});

export type PropertySchemaAssociationFormData = z.infer<typeof zPropertySchemaAssociationFormData>;

const zPropertySchemaSetCreateFormData = z.object({
  id: z.string().optional(),
  propertySchemaAssociations: z.array(zPropertySchemaAssociationFormData).optional(),
});

export type PropertySchemaSetCreateFormData = z.infer<typeof zPropertySchemaSetCreateFormData>;

export type PropertySchemaAssociationsFormDataBase = {
  propertySchemaAssociations?: PropertySchemaAssociationFormData[];
};

const zCreateDataImportProjectFormData = z.object({
  name: z.string(),
  year: z.string().nullable(),
  users: z.array(z.object({ id: z.string(), displayName: z.string().nullable(), email: z.string() })),
  documentUrl: z.string(),
  description: z.string(),
});
export const zCreateDataImportProjectFormDataKeys = zCreateDataImportProjectFormData.keyof();
export type CreateDataImportProjectFormData = z.infer<typeof zCreateDataImportProjectFormData>;

const zBootstrapFormData = zCreateDataImportProjectFormData.extend({
  references: z.array(z.object({ id: z.string(), title: z.string() })),
  dataSourceTemplate: z.object({ id: z.string(), name: z.string() }).nullable(),
  propertySchemaAssociations: z.array(zPropertySchemaAssociationFormData).optional(),
});

export const zBootstrapFormDataKeys = zBootstrapFormData.keyof();
export type BootstrapFormData = z.infer<typeof zBootstrapFormData>;

const zDataImportProjectFormSchema = z.object({
  name: z.string().min(1),
  statusId: z.string(),
  documentUrl: z.string(),
  description: z.string(),
  references: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
    }),
  ),
});

export type DataImportProjectFormData = z.infer<typeof zDataImportProjectFormSchema>;

export type DataSourceFormData = {
  name: string;
  references: Array<{ id: string; title: string }>;
  columns: SheetColumn[];
  sheets: SheetState[];
  // Per-slot variantKeys for sheets where one category is mapped by multiple
  // instances (see VariantKeySlots); keyed by sheet name, then instanceId.
  variantKeys?: SheetVariantKeys;
};

/** Visual tone shared by count badges, squares, and status chips. */
export type DipTone = "neutral" | "success" | "attention" | "error" | "primary" | "accent";

/** Per-job execution status overlaid on pipeline job nodes during a run. */
export type DipRunStatus = "queued" | "running" | "success" | "failed" | "skipped" | "dryok" | "checking";

export type DipNodeVariant = "entity" | "relation" | "record_update";

/**
 * Count-badge styles. Superset of DipTone with extra count-only variants:
 * `white` (plain), `primaryFilled` (solid blue), `primaryOutline` (blue outline).
 */
export type DipCountTone = DipTone | "white" | "primaryFilled" | "primaryOutline";

export interface DipStatusBadge {
  /** i18n key under feature.dataImportProjects.v2 (e.g. "status.clean"). */
  labelKey: string;
  tone: DipTone;
}

export interface DipNodeStat {
  /** i18n key under feature.dataImportProjects.v2. */
  labelKey: string;
  value: number;
  tone: DipTone;
}

export interface DipDataSourceStatsSummary {
  dataRowsCount: number | null;
  readyRowsCount: number | null;
}

export interface DataImportProjectListRow {
  id: string;
  seq: number;
  seqLabel: string;
  name: string;
  statusId: string;
  documentUrl: string | null;
  startAtISO: string | null;
  endAtISO: string | null;
  updatedAtISO: string;
  isDeactivated: boolean;
  references: string[];
  referencesText: string;
}

/** Column-filter draft/committed values for the Data Import Projects list. Empty string = unset. */
export interface DipColumnFilterValues {
  seq: string;
  name: string;
  status: string;
  references: string;
  startAtFrom: string;
  startAtTo: string;
  endAtFrom: string;
  endAtTo: string;
}

export const EMPTY_DIP_COLUMN_FILTER_VALUES: DipColumnFilterValues = {
  seq: "",
  name: "",
  status: "",
  references: "",
  startAtFrom: "",
  startAtTo: "",
  endAtFrom: "",
  endAtTo: "",
};

export type DipPeekType =
  | "dataSource"
  | "evidenceSet"
  | "batchEvidencesJob"
  | "batchChangeRequestsJob"
  | "evidenceDrafts"
  | "records"
  | "entityComposer"
  | "relationComposer"
  | "recordUpdateComposer"
  | "evidenceImporter";

export interface DipPeekTarget {
  type: DipPeekType;
  id: string;
  initialTab?: "info" | "changeRequests" | "evidences";
  batchChangeRequestsJobId?: string;
  recordComposerDefinitionId?: string;
  recordKind?: "entity" | "relation" | "record_update";
}

/** Row node used by all three pipeline flows. */
export interface DipItemNodeData extends Record<string, unknown> {
  count: number | null;
  countTone: DipCountTone;
  squareTone: DipTone | null;
  /** V1 detail URL for the represented resource. Non-resource aggregate nodes leave this empty. */
  detailPath?: string | null;
  /** Detail target to open in the v2 slide-over before falling back to full-page navigation. */
  peek?: DipPeekTarget | null;
  /** Nested visual-only nodes can hide handles while keeping the row styling. */
  showHandles?: boolean;
  /** Semantic visual treatment for pipeline nodes. */
  nodeKind:
    | "dataSource"
    | "batchEvidencesJob"
    | "evidenceImporter"
    | "evidenceDrafts"
    | "evidenceSet"
    | "batchChangeRequestsJob"
    | "composer"
    | "changeRequests"
    | "records";
  /** Optional visual subtype for nodes that need a finer semantic color split. */
  nodeVariant?: DipNodeVariant;
  /** Deactivated rows render dimmed when the section includes them. */
  isDeactivated?: boolean;
  /** Disabled rows are visible for context but do not represent an actionable review. */
  isDisabled?: boolean;
  /** Sample data value — rendered as a variable, not a JSX literal. */
  title: string;
  subtitle: string | null;
  rightStats?: DipNodeStat[];
  badges: DipStatusBadge[];
  /**
   * Count of corrected evidences (a newer generation exists) in this evidence set
   * whose records have not been updated yet. Renders an attention badge when > 0.
   */
  pendingCorrections?: number;
  /** Real job id (batchEvidencesJob/batchChangeRequestsJob) for run-status overlay. Job nodes only. */
  jobId?: string;
  /** Live execution status overlay; undefined when no run is in progress. */
  runStatus?: DipRunStatus;
  /** Count-delta badge text shown on success/dry-run (e.g. "+3" or "+3 (予定)"). */
  runDelta?: string | null;
}

export interface DipGroupNodeData extends Record<string, unknown> {
  label: string;
  detailPath?: string | null;
  peek?: DipPeekTarget | null;
  showHandles?: boolean;
}

/** Column header node ("Data Sources", "BEJob", ...). */
export interface DipStageHeaderNodeData extends Record<string, unknown> {
  /** i18n key under feature.dataImportProjects.v2.stage. */
  titleKey: DipPipelineStageKey;
  /** Optional fixed width for headers that need to align with grouped content. */
  width?: number;
}

/** Schema diagram node. */
export interface DipSchemaNodeData extends Record<string, unknown> {
  title: string;
  kind: "main" | "relation" | "entity";
  /** Category name of the represented composer; shown as the sub-label. Null when unresolved. */
  category: string | null;
  /** V1 composer detail URL for the represented schema node. */
  detailPath?: string | null;
}

/** A pipeline item = one DipItemNodeData plus its node id and edge target(s). */
export interface DipPipelineItem extends DipItemNodeData {
  id: string;
  /** Edge target(s) this item flows into (same-section). null = terminal in its column. */
  to: string | string[] | null;
  /** Render this item as a React Flow group node and place children inside it. */
  renderAsGroup?: boolean;
  children?: DipPipelineItem[];
  /** Align this item with a child row inside grouped rows. */
  alignToGroupChildIndex?: number;
}

type DipPipelineStageKey =
  | "dataSources"
  | "bejob"
  | "drafts"
  | "evidenceSets"
  | "bcrjob"
  | "changeRequests"
  | "records";

export interface DipPipelineColumn {
  /** i18n key suffix under feature.dataImportProjects.v2.stage. */
  titleKey: DipPipelineStageKey;
  items: DipPipelineItem[];
  layout?: "default" | "narrow";
}

export type DipSummaryTone = "dataSource" | "evidenceDrafts" | "evidenceSet" | "changeRequests" | "records";

interface DipSummaryMetric {
  /** i18n key under feature.dataImportProjects.v2.summary. */
  labelKey: string;
  value: number | null;
  tone: DipSummaryTone;
}

/** A group of output-summary metrics. Keys resolve via i18n. */
export interface DipSummaryGroup {
  /** i18n key under feature.dataImportProjects.v2.summary. */
  titleKey: string;
  metrics: DipSummaryMetric[];
}

export type DipFlowNode =
  | Node<DipItemNodeData, "dipItem">
  | Node<DipStageHeaderNodeData, "dipStageHeader">
  | Node<DipSchemaNodeData, "dipSchema">
  | Node<DipGroupNodeData, "dipGroup">;

/** One relation → target-entity pair branching off the schema's main entity. */
export interface DipSchemaPair {
  relationId: string;
  relationTitle: string;
  /** Category name of the relation composer; null when unresolved. */
  relationCategory: string | null;
  relationDetailPath: string;
  entityId: string;
  entityTitle: string;
  /** Category name of the target entity; null when unresolved. */
  entityCategory: string | null;
  entityDetailPath: string | null;
}

/** Schema diagram derived from the DIP's record composer definitions. */
export interface DipSchemaGraph {
  /** Title of the main (hub) entity; null when there are no relations. */
  mainTitle: string | null;
  /** Category name of the main (hub) entity; null when unresolved. */
  mainCategory: string | null;
  /** V1 entity composer detail URL for the main entity; null when unresolved. */
  mainDetailPath: string | null;
  pairs: DipSchemaPair[];
}

/** Normalized run-history row for the V2 UI. UI-only — not a storage shape. */
export type DipRunHistoryRow =
  | {
      kind: "pipeline";
      action: "run" | "dryrun";
      id: string; // jobRunId
      executedAtISO: string;
      status: "running" | "completed" | "failed";
      beJobIds: string[];
      bcrJobIds: string[];
      autoApprove: boolean;
      batchEvidencesJobRuns: DipPipelineJobExecution[];
      batchChangeRequestsJobRuns: DipPipelineJobExecution[];
    }
  | {
      kind: "diagnosis";
      action: "diagnosis";
      id: string; // diagnosis id
      executedAtISO: string;
      status: "pending" | "running" | "completed" | "failed";
      counts: { ok: number; warning: number; error: number; info: number };
    };

export interface DipPipelineJobExecution {
  jobId: string;
  jobRunId: string;
}

export interface DipPipelineDryRunResultTarget {
  jobRunId: string;
  batchEvidencesJobRuns: DipPipelineJobExecution[];
  batchChangeRequestsJobRuns: DipPipelineJobExecution[];
}

type DipPipelineDryRunResultSource = {
  kind?: "pipeline";
  action: "run" | "dryrun";
  id: string;
  status: "running" | "completed" | "failed";
  batchEvidencesJobRuns: DipPipelineJobExecution[];
  batchChangeRequestsJobRuns: DipPipelineJobExecution[];
};

export function toPipelineDryRunResultTarget(row: DipPipelineDryRunResultSource): DipPipelineDryRunResultTarget | null {
  if (row.action !== "dryrun" || row.status !== "completed") {
    return null;
  }

  if (row.batchEvidencesJobRuns.length === 0 && row.batchChangeRequestsJobRuns.length === 0) {
    return null;
  }

  return {
    jobRunId: row.id,
    batchEvidencesJobRuns: row.batchEvidencesJobRuns,
    batchChangeRequestsJobRuns: row.batchChangeRequestsJobRuns,
  };
}

/** Project info, transformed for the presenter. ISO dates are formatted in the component. */
export interface DipProjectInfoUI {
  title: string;
  status: string;
  statusTone: DipTone;
  updatedAtISO: string;
  /** ISO string of the "Planning" status log, or null when not started. */
  startAtISO: string | null;
  /** ISO string of the "Done" status log, or null when not finished. */
  endAtISO: string | null;
  users: string[];
  description: string | null;
}

// FIXME(tom): This should be defined in cnut and the frontend fetches it dynamically
export const EvidencePropertySchemaTypes = [
  PropertySchemaType.String,
  PropertySchemaType.Strings,
  PropertySchemaType.Number,
  PropertySchemaType.Numbers,
  PropertySchemaType.Date,
  PropertySchemaType.Gdtf,
  PropertySchemaType.Select,
  PropertySchemaType.MultiSelect,
  PropertySchemaType.Bool,
  PropertySchemaType.Url,
  // A column of GeoItem IDs. The importer resolves them against Miguel and the commit publishes
  // them, so the type has to be selectable here for that pipeline to have an entry point.
  PropertySchemaType.LinkGeoItems,
] as const satisfies PropertySchemaType[];

export type PropertySchemaSetFormData = {
  id: string;
  name: string;
  propertySchemaAssociations: PropertySchemaAssociationFormData[];
};

const zEvidenceSetEditFormData = z.object({
  name: z.string(),
  propertySchemaSetId: z.string().uuid(),
  propertySchemaAssociations: z.array(zPropertySchemaAssociationFormData).optional(),
});

export const zEvidenceSetEditFormDataKeys = zEvidenceSetEditFormData.keyof();
export type EvidenceSetEditFormData = z.infer<typeof zEvidenceSetEditFormData>;

export type FlattenedDraft = { evidenceId: string } & Record<string, string>;

// Local replacement for the formerly-generated BatchEvidencesJobType enum.
// The cnut GraphQL schema no longer exposes this type because new s3_script jobs
// cannot be created, but historical rows still carry these jobTypeId strings.
export const BatchEvidencesJobType = {
  DbBasedScript: "db_based_script",
  S3Script: "s3_script",
} as const;
export type BatchEvidencesJobType = (typeof BatchEvidencesJobType)[keyof typeof BatchEvidencesJobType];

interface DryRunHistoryItem_Base<
  T extends string,
  Parameters extends Record<string, unknown>,
  Response extends Record<string, unknown>,
> {
  dataSourceId?: string;
  jobType: T;
  jobId: string;
  jobRunId: string;
  status: "submitted" | "running" | "completed" | "failed";
  /** What kind of execution this localStorage row records. Diagnosis is NOT stored here (it comes from the
   * server-side diagnosis list and is merged in the UI). Absent on legacy rows (infer from parameters.dryRun). */
  action?: "run" | "dryrun";
  parameters: Parameters;
  response: Response;
  executedAt: string;
}

type DataImportPipelineJobHistoryItemBase = DryRunHistoryItem_Base<
  "DataImportPipelineJob",
  {
    batchEvidencesJobIds: string[];
    batchChangeRequestsJobIds: string[];
    dryRun: boolean;
    dryRunOptions: DryRunOptions | null;
    autoApprove: boolean | null;
  },
  DataImportPipelineJobSubmitOutput
>;

export type DataImportProjectPipelineJobHistoryItem = DataImportPipelineJobHistoryItemBase & {
  dataImportProjectId: string;
};

export type CategorySchemaMigrationPipelineJobHistoryItem = DataImportPipelineJobHistoryItemBase & {
  categorySchemaMigrationPlanKey: string;
};

export type DryRunHistoryItemDataImportPipelineJob =
  | DataImportProjectPipelineJobHistoryItem
  | CategorySchemaMigrationPipelineJobHistoryItem;

export type DryRunHistoryItem = DryRunHistoryItemDataImportPipelineJob;

export type DryRunHistory = DryRunHistoryItem[];

type BatchChangeRequestsJobGql = Pick<
  GetBatchChangeRequestsJobQuery["batchChangeRequestsJobs"][number],
  | "id"
  | "s3Path"
  | "name"
  | "jobTypeId"
  | "batchChangeRequestsJobRuns"
  | "batchChangeRequestsJobRecordComposerDefinitions"
  | "createdAt"
  | "deactivatedAt"
>;

type RecordComposerDefinitionGql =
  BatchChangeRequestsJobGql["batchChangeRequestsJobRecordComposerDefinitions"][number]["recordComposerDefinition"];

type EvidenceSetFromRecordComposerDefinition =
  RecordComposerDefinitionGql["recordComposerDefinitionEvidenceSets"][number]["evidenceSet"];

type JobWithRecordComposers = Pick<
  BatchChangeRequestsJob_DefaultFragment,
  "batchChangeRequestsJobRecordComposerDefinitions"
>;

export const evidenceSetsOfBatchChangeRequestsJob = (
  job: JobWithRecordComposers,
): EvidenceSetFromRecordComposerDefinition[] => {
  const map = new Map<string, EvidenceSetFromRecordComposerDefinition>();
  job.batchChangeRequestsJobRecordComposerDefinitions.forEach(({ recordComposerDefinition }) => {
    recordComposerDefinition.recordComposerDefinitionEvidenceSets.forEach(({ evidenceSet }) => {
      map.set(evidenceSet.id, evidenceSet);
    });
  });
  return Array.from(map.values());
};

export type BatchEvidencesJobFormData = {
  name: string;
  evidenceImporterId: string;
};

export type BatchChangeRequestJobsFormData = {
  name: string;
  jobType: BatchChangeRequestsJobType;
  recordComposerDefinitionIds: string[];
};

const zDataSourceEditFormData = z.object({
  name: z.string(),
  references: z.array(z.object({ id: z.string(), title: z.string() })),
  propertySchemaAssociations: z.array(zPropertySchemaAssociationFormData).optional(),
});
export const zDataSourceEditFormDataKeys = zDataSourceEditFormData.keyof();
export type DataSourceEditFormData = z.infer<typeof zDataSourceEditFormData>;

const zDataSourceSheetIdEditFormData = z.object({
  sheetId: z.string(),
});
export const zDataSourceSheetIdEditFormDataKeys = zDataSourceSheetIdEditFormData.keyof();
export type DataSourceSheetIdEditFormData = z.infer<typeof zDataSourceSheetIdEditFormData>;
